import {
    Badge,
    Box,
    Center,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Select,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tooltip,
    Tr,
} from '@chakra-ui/react';
import { useMemo, useState, Fragment } from 'react';
import {
    BusinessLine,
    InventoryAdjustmentCreateType,
    InventoryAdjustmentType,
    InventoryItemEntry,
    InventoryStockAdjustmentEntry,
} from '../../types/inventory';
import { useGetInventoryStockAdjustments } from '../../hooks/inventory/useGetInventoryStockAdjustments';
import { useGetSettings } from '../../hooks/useGetSettings';
import {
    formatBusinessLineDisplay,
    formatStockAdjustmentRoDisplay,
} from '../../utils/inventoryConstants';
import { formatInventoryActivityTime } from './inventoryActivityLogShared';
import { formatPrice } from '../../functions';
import CurrencyInput from './CurrencyInput';
import QuantityInputField from './QuantityInputField';
import RoNumberField from './RoNumberField';
import { Button, Icon, IconButton } from '../../ui';

const COLLAPSED_LIMIT = 5;
const NOTE_MAX_LENGTH = 1000;

export { COLLAPSED_LIMIT };

export interface PendingStockAdjustmentCreate {
    /** Client-only id for list keying / discard. */
    clientId: string;
    type: InventoryAdjustmentCreateType;
    quantity: number;
    businessLine: BusinessLine;
    roNumber?: string;
    unitCost?: number;
    note?: string;
}

/** Staged note edits for persisted adjustments. Empty string clears the note. */
export type PendingStockAdjustmentNoteUpdates = Record<string, string>;

export function createPendingAdjustmentId(): string {
    return `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getActorName(adj: InventoryStockAdjustmentEntry): string {
    if (adj.userName) {
        return `${adj.userName.firstName} ${adj.userName.lastName}`.trim();
    }
    return 'System';
}

function getAdjustmentTypeMeta(type: InventoryAdjustmentType) {
    if (type === 'order') {
        return { label: 'Order', colorScheme: 'green' as const };
    }
    if (type === 'sale') {
        return { label: 'Sale', colorScheme: 'blue' as const };
    }
    if (type === 'initial') {
        return { label: 'Initial', colorScheme: 'purple' as const };
    }
    return { label: 'Usage', colorScheme: 'orange' as const };
}

function formatUnitCost(adj: { unitCost?: number }): string {
    return adj.unitCost != null ? formatPrice(adj.unitCost) : '—';
}

function normalizeNote(value: string): string {
    return value.trim().slice(0, NOTE_MAX_LENGTH);
}

/** Newest-first: delete only on the top row, or the next row when the one above is pending delete. */
function canRevealDeleteAction(
    index: number,
    adjustments: InventoryStockAdjustmentEntry[],
    pendingDeleteIds: string[],
): boolean {
    if (index < 0 || index >= adjustments.length) return false;
    if (pendingDeleteIds.includes(adjustments[index]._id)) return true;
    if (index === 0) return true;
    return pendingDeleteIds.includes(adjustments[index - 1]._id);
}

type ExpandedNoteTarget =
    | { kind: 'pending'; clientId: string }
    | { kind: 'persisted'; adjustmentId: string }
    | null;

interface StockAdjustmentNoteEditorProps {
    value: string;
    onChange: (value: string) => void;
    onDone: () => void;
    onCancel: () => void;
}

/** Expandable note editor for history-row add/edit. */
function StockAdjustmentNoteEditor({
    value,
    onChange,
    onDone,
    onCancel,
}: StockAdjustmentNoteEditorProps) {
    return (
        <Box mt={2}>
                <Textarea
                    size="sm"
                    value={value}
                    onChange={(e) => onChange(e.target.value.slice(0, NOTE_MAX_LENGTH))}
                    placeholder="Add a note..."
                    rows={1}
                    resize="none"
                    maxLength={NOTE_MAX_LENGTH}
                />
            <HStack spacing={2} mt={2} justify="flex-end">
                <Button size="sm" variant="outlineGray" onClick={onCancel}>
                    Cancel
                </Button>
                <Button size="sm" onClick={onDone}>
                    Done
                </Button>
            </HStack>
        </Box>
    );
}

interface InventoryStockAdjustmentsSectionProps {
    inventoryItemId: string;
    item: InventoryItemEntry;
    enabled?: boolean;
    pendingCreates: PendingStockAdjustmentCreate[];
    pendingDeleteIds: string[];
    pendingNoteUpdates: PendingStockAdjustmentNoteUpdates;
    onPendingCreatesChange: (drafts: PendingStockAdjustmentCreate[]) => void;
    onPendingDeleteIdsChange: (ids: string[]) => void;
    onPendingNoteUpdatesChange: (updates: PendingStockAdjustmentNoteUpdates) => void;
}

const InventoryStockAdjustmentsSection = ({
    inventoryItemId,
    item,
    enabled = true,
    pendingCreates,
    pendingDeleteIds,
    pendingNoteUpdates,
    onPendingCreatesChange,
    onPendingDeleteIdsChange,
    onPendingNoteUpdatesChange,
}: InventoryStockAdjustmentsSectionProps) => {
    const [expanded, setExpanded] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [adjustmentType, setAdjustmentType] = useState<InventoryAdjustmentCreateType>('order');
    const [quantity, setQuantity] = useState(0);
    const [unitCost, setUnitCost] = useState(0);
    const [businessLineValue, setBusinessLineValue] = useState('');
    const [roNumberValue, setRoNumberValue] = useState('');
    const [addNote, setAddNote] = useState('');
    const [businessLineError, setBusinessLineError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [expandedNote, setExpandedNote] = useState<ExpandedNoteTarget>(null);
    const [noteDraft, setNoteDraft] = useState('');

    const { data: settingsData } = useGetSettings();
    const businessLineOptions = useMemo(
        () => settingsData?.inventory?.businessLines ?? [],
        [settingsData?.inventory?.businessLines],
    );
    const defaultBusinessLine = settingsData?.inventory?.defaultBusinessLine ?? '';

    const limit = expanded ? 100 : COLLAPSED_LIMIT;
    const { data, isLoading, isError } = useGetInventoryStockAdjustments(
        inventoryItemId,
        limit,
        0,
        enabled,
    );

    const adjustments = useMemo(() => {
        const list = data?.adjustments ?? [];
        return [...list].sort(
            (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );
    }, [data?.adjustments]);
    const totalCount = data?.totalCount ?? 0;

    const showMoneyField = adjustmentType === 'order' || adjustmentType === 'sale';
    const moneyFieldLabel = 'Unit Cost';

    const resetAddForm = () => {
        setIsAdding(false);
        setAdjustmentType('order');
        setQuantity(0);
        setUnitCost(item.averageUnitCost);
        setBusinessLineValue('');
        setRoNumberValue('');
        setAddNote('');
        setBusinessLineError(null);
        setFormError(null);
    };

    const resolvedDefaultBusinessLine = useMemo(() => {
        const names = businessLineOptions.map((o) => o.name);
        if (defaultBusinessLine && names.includes(defaultBusinessLine)) {
            return defaultBusinessLine;
        }
        return '';
    }, [businessLineOptions, defaultBusinessLine]);

    const openAddForm = () => {
        setUnitCost(item.averageUnitCost);
        setAdjustmentType('order');
        setQuantity(0);
        setBusinessLineValue(resolvedDefaultBusinessLine);
        setRoNumberValue('');
        setAddNote('');
        setBusinessLineError(null);
        setFormError(null);
        setExpandedNote(null);
        setIsAdding(true);
    };

    const handleStageCreate = () => {
        if (quantity <= 0) {
            setFormError('Enter a quantity greater than zero.');
            return;
        }
        if (showMoneyField && unitCost < 0) {
            setFormError('Unit cost must be zero or greater.');
            return;
        }
        const businessLine = businessLineValue.trim();
        if (!businessLine) {
            setBusinessLineError('Business line is required.');
            return;
        }
        setBusinessLineError(null);
        setFormError(null);
        const roNumber = roNumberValue.trim();
        const note = normalizeNote(addNote);
        onPendingCreatesChange([
            ...pendingCreates,
            {
                clientId: createPendingAdjustmentId(),
                type: adjustmentType,
                quantity,
                businessLine,
                ...(roNumber ? { roNumber } : {}),
                ...(showMoneyField ? { unitCost } : {}),
                ...(note ? { note } : {}),
            },
        ]);
        resetAddForm();
    };

    const discardPendingCreate = (clientId: string) => {
        onPendingCreatesChange(pendingCreates.filter((d) => d.clientId !== clientId));
        if (expandedNote?.kind === 'pending' && expandedNote.clientId === clientId) {
            setExpandedNote(null);
            setNoteDraft('');
        }
    };

    const togglePendingDelete = (adjustmentId: string) => {
        const index = adjustments.findIndex((adj) => adj._id === adjustmentId);
        if (index < 0) return;

        if (pendingDeleteIds.includes(adjustmentId)) {
            onPendingDeleteIdsChange(
                pendingDeleteIds.filter((id) => {
                    const idIndex = adjustments.findIndex((adj) => adj._id === id);
                    return idIndex >= 0 && idIndex < index;
                }),
            );
            return;
        }

        if (!canRevealDeleteAction(index, adjustments, pendingDeleteIds)) return;
        onPendingDeleteIdsChange([...pendingDeleteIds, adjustmentId]);
    };

    const effectivePendingNote = (draft: PendingStockAdjustmentCreate): string =>
        draft.note ?? '';

    const effectivePersistedNote = (adj: InventoryStockAdjustmentEntry): string => {
        if (Object.prototype.hasOwnProperty.call(pendingNoteUpdates, adj._id)) {
            return pendingNoteUpdates[adj._id] ?? '';
        }
        return adj.note ?? '';
    };

    const openPendingNote = (draft: PendingStockAdjustmentCreate) => {
        setIsAdding(false);
        setExpandedNote({ kind: 'pending', clientId: draft.clientId });
        setNoteDraft(effectivePendingNote(draft));
    };

    const openPersistedNote = (adj: InventoryStockAdjustmentEntry) => {
        setIsAdding(false);
        setExpandedNote({ kind: 'persisted', adjustmentId: adj._id });
        setNoteDraft(effectivePersistedNote(adj));
    };

    const closeNoteEditor = () => {
        setExpandedNote(null);
        setNoteDraft('');
    };

    const handleNoteDone = () => {
        if (!expandedNote) return;
        const next = normalizeNote(noteDraft);
        if (expandedNote.kind === 'pending') {
            onPendingCreatesChange(
                pendingCreates.map((d) => {
                    if (d.clientId !== expandedNote.clientId) return d;
                    if (!next) {
                        const { note: _removed, ...rest } = d;
                        void _removed;
                        return rest;
                    }
                    return { ...d, note: next };
                }),
            );
        } else {
            const adj = adjustments.find((a) => a._id === expandedNote.adjustmentId);
            const original = adj?.note ?? '';
            const nextUpdates = { ...pendingNoteUpdates };
            if (next === original) {
                delete nextUpdates[expandedNote.adjustmentId];
            } else {
                nextUpdates[expandedNote.adjustmentId] = next;
            }
            onPendingNoteUpdatesChange(nextUpdates);
        }
        closeNoteEditor();
    };

    const handleNoteCancel = () => {
        closeNoteEditor();
    };

    const canExpand = totalCount > COLLAPSED_LIMIT;
    const hasAnyRows = pendingCreates.length > 0 || adjustments.length > 0;

    const renderNoteCell = (
        hasNote: boolean,
        isOpen: boolean,
        onToggle: () => void,
    ) => (
        <Tooltip label="Add Note" hasArrow>
            <IconButton
                aria-label={hasNote ? 'Edit note' : 'Add note'}
                icon={
                    <Icon
                        name="edit"
                        size="sm"
                        color={hasNote ? 'brand.solid' : 'fg.muted'}
                    />
                }
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={onToggle}
                aria-expanded={isOpen}
            />
        </Tooltip>
    );

    const renderDeleteCell = (opts: {
        show: boolean;
        isPendingDelete?: boolean;
        onClick: () => void;
        label: string;
        isUndo?: boolean;
    }) => {
        if (!opts.show) return null;
        return (
            <IconButton
                aria-label={opts.label}
                icon={
                    <Icon
                        name="trash"
                        size="sm"
                        color={opts.isUndo ? 'fg.muted' : 'error.fg'}
                    />
                }
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={opts.onClick}
            />
        );
    };

    const colCount = 9;

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={3}>
                <Text fontSize="sm" fontWeight="bold" color="fg.default">
                    Stock Adjustment History
                </Text>
                {!isAdding && (
                    <Button size="sm" onClick={openAddForm}>
                        + Adjust Stock
                    </Button>
                )}
            </Flex>

            {isAdding && (
                <Box
                    mb={4}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="border.default"
                    overflow="hidden"
                >
                    <Flex mb={3} align="flex-start" overflow="hidden" flexWrap="wrap" gap={3}>
                        <Box
                            overflow="hidden"
                            flexShrink={0}
                            maxW={showMoneyField ? '180px' : '0px'}
                            transition="max-width 0.28s ease"
                            pointerEvents={showMoneyField ? 'auto' : 'none'}
                            aria-hidden={!showMoneyField}
                        >
                            <Box
                                minW="160px"
                                opacity={showMoneyField ? 1 : 0}
                                transform={
                                    showMoneyField ? 'translateX(0)' : 'translateX(-100%)'
                                }
                                transition="opacity 0.22s ease, transform 0.28s ease"
                            >
                                <FormControl>
                                    <FormLabel fontSize="sm">{moneyFieldLabel}</FormLabel>
                                    <CurrencyInput
                                        value={unitCost}
                                        onChange={setUnitCost}
                                        useThousandsSeparator
                                    />
                                </FormControl>
                            </Box>
                        </Box>
                        <FormControl flex={1} minW="100px">
                            <FormLabel fontSize="sm">Quantity</FormLabel>
                            <QuantityInputField
                                value={quantity}
                                onChange={(val) => setQuantity(val ?? 0)}
                            />
                        </FormControl>
                        <FormControl flex={1} minW="140px" isInvalid={!!businessLineError}>
                            <FormLabel fontSize="sm">Business Line</FormLabel>
                            <Select
                                size="sm"
                                placeholder="Select business line"
                                value={businessLineValue}
                                onChange={(e) => {
                                    setBusinessLineValue(e.target.value);
                                    setBusinessLineError(null);
                                }}
                            >
                                {businessLineOptions.map((opt) => (
                                    <option key={opt.name} value={opt.name}>
                                        {opt.name}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{businessLineError}</FormErrorMessage>
                        </FormControl>
                        <FormControl flex={1} minW="120px">
                            <FormLabel fontSize="sm">RO #</FormLabel>
                            <RoNumberField value={roNumberValue} onChange={setRoNumberValue} />
                        </FormControl>
                    </Flex>
                    <FormControl mb={0}>
                        <FormLabel fontSize="sm">Note</FormLabel>
                        <Textarea
                            size="sm"
                            value={addNote}
                            onChange={(e) =>
                                setAddNote(e.target.value.slice(0, NOTE_MAX_LENGTH))
                            }
                            placeholder="Add a note..."
                            rows={1}
                            resize="none"
                            maxLength={NOTE_MAX_LENGTH}
                        />
                    </FormControl>
                    {formError && (
                        <Text fontSize="sm" color="error.fg" mb={2} mt={2}>
                            {formError}
                        </Text>
                    )}
                    <Flex justify="space-between" align="center" flexWrap="wrap" gap={3} mt={3}>
                        <HStack spacing={0} bg="bg.muted" borderRadius="md" p="2px">
                            {(
                                [
                                    { value: 'order' as const, label: 'Order' },
                                    { value: 'usage' as const, label: 'Usage' },
                                    { value: 'sale' as const, label: 'Sale' },
                                ] as const
                            ).map((option) => {
                                const isActive = adjustmentType === option.value;
                                return (
                                    <Box
                                        key={option.value}
                                        as="button"
                                        type="button"
                                        px={3}
                                        py={1}
                                        borderRadius="md"
                                        bg={isActive ? 'bg.surface' : 'transparent'}
                                        boxShadow={isActive ? 'sm' : 'none'}
                                        fontSize="sm"
                                        fontWeight="medium"
                                        color={isActive ? 'fg.default' : 'fg.muted'}
                                        transition="all 0.15s ease"
                                        _hover={!isActive ? { color: 'fg.default' } : undefined}
                                        onClick={() => {
                                            if (!isActive) {
                                                setAdjustmentType(option.value);
                                                if (
                                                    option.value === 'order' ||
                                                    option.value === 'sale'
                                                ) {
                                                    setUnitCost(item.averageUnitCost);
                                                }
                                            }
                                        }}
                                    >
                                        {option.label}
                                    </Box>
                                );
                            })}
                        </HStack>
                        <HStack spacing={2}>
                            <Button size="sm" variant="outlineGray" onClick={resetAddForm}>
                                Cancel
                            </Button>
                            <Button size="sm" onClick={handleStageCreate}>
                                Add
                            </Button>
                        </HStack>
                    </Flex>
                </Box>
            )}

            {isLoading && (
                <Center py={8}>
                    <Spinner size="md" color="brand.solid" />
                </Center>
            )}
            {isError && (
                <Text fontSize="sm" color="error.fg" py={4}>
                    Failed to load stock adjustments
                </Text>
            )}
            {!isLoading && !isError && !hasAnyRows && (
                <Text fontSize="sm" color="fg.muted" fontStyle="italic" py={2}>
                    No adjustments yet
                </Text>
            )}
            {!isLoading && !isError && hasAnyRows && (
                <>
                    <Box borderWidth="1px" borderColor="border.subtle" borderRadius="md">
                        <Table
                            size="sm"
                            variant="simple"
                            sx={{
                                'th, td': { px: 1.5 },
                            }}
                        >
                            <Thead bg="bg.muted">
                                <Tr>
                                    <Th w="72px">Type</Th>
                                    <Th>Date</Th>
                                    <Th w="132px">Business Line</Th>
                                    <Th w="90px">RO #</Th>
                                    <Th w="90px" isNumeric whiteSpace="nowrap">
                                        In-Stock
                                    </Th>
                                    <Th w="56px" isNumeric>
                                        Qty
                                    </Th>
                                    <Th isNumeric>Cost</Th>
                                    <Th w="110px">User</Th>
                                    <Th w="72px" textAlign="right">
                                        Actions
                                    </Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {pendingCreates.map((draft) => {
                                    const meta = getAdjustmentTypeMeta(draft.type);
                                    const note = effectivePendingNote(draft);
                                    const hasNote = note.length > 0;
                                    const isNoteOpen =
                                        expandedNote?.kind === 'pending' &&
                                        expandedNote.clientId === draft.clientId;
                                    return (
                                        <Fragment key={draft.clientId}>
                                            <Tr bg="blue.50">
                                                <Td>
                                                    <Badge
                                                        colorScheme={meta.colorScheme}
                                                        fontSize="xs"
                                                        textTransform="none"
                                                    >
                                                        {meta.label}
                                                    </Badge>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="xs" color="fg.muted">
                                                        Not saved
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    <Tooltip
                                                        label={
                                                            formatBusinessLineDisplay(
                                                                draft.businessLine,
                                                            ) || '—'
                                                        }
                                                        placement="top"
                                                    >
                                                        <Text fontSize="sm" noOfLines={1}>
                                                            {formatBusinessLineDisplay(
                                                                draft.businessLine,
                                                            ) || '—'}
                                                        </Text>
                                                    </Tooltip>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="sm" noOfLines={1}>
                                                        {formatStockAdjustmentRoDisplay(
                                                            draft.roNumber,
                                                        )}
                                                    </Text>
                                                </Td>
                                                <Td isNumeric>—</Td>
                                                <Td isNumeric>{draft.quantity}</Td>
                                                <Td isNumeric>{formatUnitCost(draft)}</Td>
                                                <Td>—</Td>
                                                <Td>
                                                    <HStack spacing={0} justify="flex-end">
                                                        {renderNoteCell(
                                                            hasNote,
                                                            isNoteOpen,
                                                            () => {
                                                                if (isNoteOpen) {
                                                                    closeNoteEditor();
                                                                } else {
                                                                    openPendingNote(draft);
                                                                }
                                                            },
                                                        )}
                                                        {renderDeleteCell({
                                                            show: true,
                                                            onClick: () =>
                                                                discardPendingCreate(
                                                                    draft.clientId,
                                                                ),
                                                            label: 'Discard',
                                                        })}
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                            {isNoteOpen && (
                                                <Tr bg="blue.50">
                                                    <Td colSpan={colCount} py={2}>
                                                        <StockAdjustmentNoteEditor
                                                            value={noteDraft}
                                                            onChange={setNoteDraft}
                                                            onDone={handleNoteDone}
                                                            onCancel={handleNoteCancel}
                                                        />
                                                    </Td>
                                                </Tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                                {adjustments.map((adj, index) => {
                                    const meta = getAdjustmentTypeMeta(adj.type);
                                    const isPendingDelete = pendingDeleteIds.includes(adj._id);
                                    const showDelete = canRevealDeleteAction(
                                        index,
                                        adjustments,
                                        pendingDeleteIds,
                                    );
                                    const actorName = getActorName(adj);
                                    const note = effectivePersistedNote(adj);
                                    const hasNote = note.length > 0;
                                    const isNoteOpen =
                                        expandedNote?.kind === 'persisted' &&
                                        expandedNote.adjustmentId === adj._id;
                                    return (
                                        <Fragment key={adj._id}>
                                            <Tr
                                                opacity={isPendingDelete ? 0.55 : 1}
                                                textDecoration={
                                                    isPendingDelete ? 'line-through' : undefined
                                                }
                                            >
                                                <Td>
                                                    <Badge
                                                        colorScheme={meta.colorScheme}
                                                        fontSize="xs"
                                                        textTransform="none"
                                                    >
                                                        {meta.label}
                                                    </Badge>
                                                </Td>
                                                <Td whiteSpace="nowrap">
                                                    <Text fontSize="xs" color="fg.muted">
                                                        {formatInventoryActivityTime(
                                                            adj.timestamp,
                                                        )}
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    <Tooltip
                                                        label={
                                                            formatBusinessLineDisplay(
                                                                adj.businessLine,
                                                            ) || '—'
                                                        }
                                                        placement="top"
                                                    >
                                                        <Text fontSize="sm" noOfLines={1}>
                                                            {formatBusinessLineDisplay(
                                                                adj.businessLine,
                                                            ) || '—'}
                                                        </Text>
                                                    </Tooltip>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="sm" noOfLines={1}>
                                                        {formatStockAdjustmentRoDisplay(
                                                            adj.roNumber,
                                                        )}
                                                    </Text>
                                                </Td>
                                                <Td isNumeric>{adj.inStockQuantityAfter}</Td>
                                                <Td isNumeric>{adj.quantity}</Td>
                                                <Td isNumeric>{formatUnitCost(adj)}</Td>
                                                <Td>
                                                    <Tooltip label={actorName} placement="top">
                                                        <Text fontSize="sm" noOfLines={1}>
                                                            {actorName}
                                                        </Text>
                                                    </Tooltip>
                                                </Td>
                                                <Td>
                                                    <HStack spacing={0} justify="flex-end">
                                                        {renderNoteCell(
                                                            hasNote,
                                                            isNoteOpen,
                                                            () => {
                                                                if (isNoteOpen) {
                                                                    closeNoteEditor();
                                                                } else {
                                                                    openPersistedNote(adj);
                                                                }
                                                            },
                                                        )}
                                                        {renderDeleteCell({
                                                            show: showDelete,
                                                            isPendingDelete,
                                                            isUndo: isPendingDelete,
                                                            onClick: () =>
                                                                togglePendingDelete(adj._id),
                                                            label: isPendingDelete
                                                                ? 'Undo delete'
                                                                : 'Delete',
                                                        })}
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                            {isNoteOpen && (
                                                <Tr>
                                                    <Td colSpan={colCount} py={2}>
                                                        <StockAdjustmentNoteEditor
                                                            value={noteDraft}
                                                            onChange={setNoteDraft}
                                                            onDone={handleNoteDone}
                                                            onCancel={handleNoteCancel}
                                                        />
                                                    </Td>
                                                </Tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                    {canExpand && (
                        <Flex justify="center" mt={2}>
                            <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="gray"
                                rightIcon={
                                    <Icon
                                        name={expanded ? 'chevronUp' : 'chevronDown'}
                                        size="sm"
                                    />
                                }
                                onClick={() => setExpanded((prev) => !prev)}
                            >
                                {expanded ? 'Show less' : `Show all (${totalCount})`}
                            </Button>
                        </Flex>
                    )}
                </>
            )}
        </Box>
    );
};

export default InventoryStockAdjustmentsSection;
